<template>
  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
    <Select
      v-model="stationId"
      :label="t('location')"
      :options="stationOptions"
      empty-value-to="null"
      class="max-sm:col-span-2"
    />
    <div class="relative">
      <div ref="startField" @click.prevent="toggleEdge('start')">
        <Input
          :label="t('startDate')"
          :value="formattedStartDate"
          readonly
          :data-focus="showPicker && activeEdge === 'start' ? '' : undefined"
        />
      </div>
      <div v-if="showPicker" ref="pickerContainer" :style="floatingStyles" class="z-[1100]">
        <Datepicker
          v-model="dates"
          v-model:active-edge="activeEdge"
          range
          inline
          week-numbers
          :clearable="false"
          enable-time-picker
        />
      </div>
    </div>
    <div ref="endField" @click.prevent="toggleEdge('end')">
      <Input
        :label="t('endDate')"
        :value="formattedEndDate"
        readonly
        :data-focus="showPicker && activeEdge === 'end' ? '' : undefined"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getDateTimeFormat } from '@/hooks/use-date-format';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { computed, ref, useTemplateRef, watch } from 'vue';
import Datepicker from '@/components/Datepicker.vue';
import { useRangePickerFields } from '@/components/datepicker/use-range-picker-fields';
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';
import type { Station } from '@/entities/station/station.entity';
import { CUSTOM_LOCATIONS, type CustomLocations } from '@/constants/custom-locations.constant';
import { useLocalizedFields } from '@/hooks/use-localized-fields';

export interface StationAndTimes {
  station: Station | CustomLocations | null;
  startDate: Date;
  endDate: Date;
}

const props = defineProps<StationAndTimes & { stations: Station[] }>();

const emit = defineEmits<{
  (e: 'change', value: StationAndTimes): void;
}>();

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const pickerContainer = useTemplateRef<HTMLElement>('pickerContainer');
const startField = useTemplateRef<HTMLElement>('startField');
const endField = useTemplateRef<HTMLElement>('endField');

const { dates, showPicker, activeEdge, toggleEdge, floatingStyles } = useRangePickerFields({
  startDate: () => props.startDate,
  endDate: () => props.endDate,
  pickerContainer,
  fields: [startField, endField],
  onCommit: (startDate, endDate) => emit('change', { station: props.station, startDate, endDate }),
});
const stationId = ref(props.station !== CUSTOM_LOCATIONS ? props.station?.id : null);

watch(stationId, () => {
  emit('change', {
    station:
      stationId.value === CUSTOM_LOCATIONS
        ? CUSTOM_LOCATIONS
        : (props.stations.find((station) => station.id === stationId.value) ?? null),
    startDate: props.startDate,
    endDate: props.endDate,
  });
});

const formattedStartDate = useFormattedDate(() => dates.value[0], getDateTimeFormat());
const formattedEndDate = useFormattedDate(() => dates.value[1], getDateTimeFormat());

const stationOptions = computed(() => [
  {
    label: t('chooseLocation'),
    value: null,
  },
  ...(props.stations.map((station) => ({
    label: getLocalizedField(station.info),
    value: station.id,
  })) ?? []),
  {
    label: t('customLocations'),
    value: CUSTOM_LOCATIONS,
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "chooseLocation": "Choose a location",
    "location": "Location",
    "startDate": "Start date",
    "endDate": "End date",
    "customLocations": "Custom locations"
  },
  "de": {
    "chooseLocation": "Ort auswählen",
    "location": "Ort",
    "startDate": "Startdatum",
    "endDate": "Enddatum",
    "customLocations": "Andere Orte"
  },
  "es": {
    "chooseLocation": "Elija una ubicación",
    "location": "Ubicación",
    "startDate": "Fecha de inicio",
    "endDate": "Fecha de fin",
    "customLocations": "Ubicaciones personalizadas"
  }
}
</i18n>
